import { Box, Card, CardContent, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import AdminDashboardLayout from "../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useFirestore } from "reactfire";
import useAdminAccess from "../../../hooks/use_admin_access";
import { MAIN_THEME_COLOR } from "../../../providers/theme/colors/colors";

function Dashboard() {
  const firestore = useFirestore();
  const { isSuperAdmin } = useAdminAccess();
  const [loginAuditList, setLoginAuditList] = useState<any[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadAuditLogs() {
      if (!isSuperAdmin) {
        if (isMounted) {
          setLoginAuditList([]);
          setIsAuditLoading(false);
        }
        return;
      }

      setIsAuditLoading(true);

      try {
        const auditQuery = query(
          collection(firestore, "admin_login_audit"),
          orderBy("loginAt", "desc"),
          limit(50),
        );
        const auditSnapshot = await getDocs(auditQuery);

        if (!isMounted) {
          return;
        }

        setLoginAuditList(
          auditSnapshot.docs.map((doc) => ({
            NO_ID_FIELD: doc.id,
            ...doc.data(),
          })),
        );
      } catch (error) {
        console.error("Failed to load admin audit logs", error);

        if (isMounted) {
          setLoginAuditList([]);
        }
      } finally {
        if (isMounted) {
          setIsAuditLoading(false);
        }
      }
    }

    loadAuditLogs();

    return () => {
      isMounted = false;
    };
  }, [firestore, isSuperAdmin]);

  const formatLoginTime = (item: any) => {
    if (item?.loginAt?.seconds) {
      return new Date(
        item.loginAt.seconds * 1000 + (item.loginAt.nanoseconds || 0) / 1000000,
      ).toLocaleString();
    }

    if (item?.loginAtClient) {
      return new Date(item.loginAtClient).toLocaleString();
    }

    return "-";
  };

  return (
    <AdminDashboardLayout>
      <Box px={3} pb={3}>
        <Typography variant="h5" fontWeight={700} mb={1}>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Monitor admin sign-ins and check recent portal access activity.
        </Typography>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} color={MAIN_THEME_COLOR} mb={1}>
              Login Audit Trail
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Every successful admin login is recorded here with date and time.
            </Typography>

            {!isSuperAdmin ? (
              <Typography variant="body2" color="text.secondary">
                Audit logs are visible only to the main admin.
              </Typography>
            ) : isAuditLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : loginAuditList.length ? (
              <Box width="100%" overflow="auto">
                <Grid container px={1} py={1.5} minWidth={720}>
                  <Grid item xs={3}>
                    <Typography fontWeight={700} fontSize={13} color={MAIN_THEME_COLOR}>
                      Time
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight={700} fontSize={13} color={MAIN_THEME_COLOR}>
                      Name
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight={700} fontSize={13} color={MAIN_THEME_COLOR}>
                      Email
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography fontWeight={700} fontSize={13} color={MAIN_THEME_COLOR}>
                      Access
                    </Typography>
                  </Grid>
                </Grid>

                {loginAuditList.map((item: any, index: number) => (
                  <Box key={item.NO_ID_FIELD || index}>
                    <Divider />
                    <Grid container px={1} py={2} minWidth={720}>
                      <Grid item xs={3}>
                        <Typography fontSize={13}>{formatLoginTime(item)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography fontSize={13}>{item?.name || "-"}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography fontSize={13}>{item?.email_id || "-"}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography fontSize={13}>
                          {item?.role || item?.modules?.join(", ") || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No login activity recorded yet.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminDashboardLayout>
  );
}

export default Dashboard;
